import React, { useState, useEffect } from "react";
import { Slider } from "../../../components/ui/Slider";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../../components/ui/Card";
import { Badge } from "../../../components/ui/Badge";

interface PlanRecommendationProps {
    onRecommendationChange?: (plan: "basic" | "plus" | "pro") => void;
}

const PlanRecommendation: React.FC<PlanRecommendationProps> = ({
    onRecommendationChange,
}) => {
    const [practiceAmount, setPracticeAmount] = useState([5]);
    const [interviewsPerMonth, setInterviewsPerMonth] = useState([2]);

    // Calculate recommended plan based on user needs
    const calculateRecommendedPlan = () => {
        // If user needs more than 50 practice cases or more than 5 interviews
        if (practiceAmount[0] > 50 || interviewsPerMonth[0] > 5) {
            return "pro";
        }
        // If user needs more than 2 practice cases or more than 2 interviews
        else if (practiceAmount[0] > 2 || interviewsPerMonth[0] > 1) {
            return "plus";
        }
        // Basic plan for minimal needs
        return "basic";
    };

    const recommendedPlan = calculateRecommendedPlan();

    useEffect(() => {
        onRecommendationChange?.(recommendedPlan);
    }, [recommendedPlan, onRecommendationChange]);

    const getPlanBadgeStyle = () => {
        switch (recommendedPlan) {
            case "pro":
                return "bg-orange-100 text-orange-800 border-orange-200";
            case "plus":
                return "bg-green-100 text-green-800 border-green-200";
            default:
                return "bg-purple-100 text-purple-800 border-purple-200";
        }
    };

    return (
        <Card className="mb-8">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-semibold text-gray-900">
                        Find Your Perfect Plan
                    </CardTitle>
                    <Badge className={getPlanBadgeStyle()}>
                        {recommendedPlan === "basic"
                            ? "Basic Plan Recommended"
                            : recommendedPlan === "plus"
                            ? "Plus Plan Recommended"
                            : "Pro Plan Recommended"}
                    </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                    Adjust the sliders below to see which plan best fits your
                    needs
                </p>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-base font-medium text-gray-700">
                            Practice Cases per Month
                        </label>
                        <span className="text-base font-semibold text-gray-900">
                            {practiceAmount[0]} cases
                        </span>
                    </div>
                    <Slider
                        value={practiceAmount}
                        onValueChange={setPracticeAmount}
                        max={100}
                        min={1}
                        step={5}
                        className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                        <span>1 case</span>
                        <span>50 cases</span>
                        <span>100 cases</span>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-base font-medium text-gray-700">
                            Expected Interviews per Month
                        </label>
                        <span className="text-base font-semibold text-gray-900">
                            {interviewsPerMonth[0]} interviews
                        </span>
                    </div>
                    <Slider
                        value={interviewsPerMonth}
                        onValueChange={setInterviewsPerMonth}
                        max={10}
                        min={0}
                        step={1}
                        className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                        <span>0 interview</span>
                        <span>5 interviews</span>
                        <span>10 interviews</span>
                    </div>
                </div>

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Why this plan?
                    </h4>
                    {recommendedPlan === "basic" && (
                        <p className="text-sm text-gray-600">
                            The Basic plan is perfect for your needs, offering
                            up to 5 practice cases and 2 interviews per month.
                        </p>
                    )}
                    {recommendedPlan === "plus" && (
                        <p className="text-sm text-gray-600">
                            The Plus plan gives you up to 50 practice cases and
                            5 interviews per month, ideal for your preparation
                            intensity.
                        </p>
                    )}
                    {recommendedPlan === "pro" && (
                        <p className="text-sm text-gray-600">
                            The Pro plan offers unlimited practice cases and
                            interviews, perfect for intensive interview
                            preparation.
                        </p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default PlanRecommendation;
